import { BBDTag, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { Card, Form, Input, Select, Switch, theme, Tooltip } from 'antd'
import * as React from 'react'
import type { FilteredMetadata } from '@api/useFreight/useFreight'
import { getRuleIcon } from '../utils'
import { NIGHT_TIME_RANGE, WEEKEND_TIME_RANGE } from './constants'

interface ServiceRuleCardProps {
  serviceRuleEnabled: boolean
  serviceRuleType?: string
  metadataOptions?: FilteredMetadata
  holidays: any[]
  isHolidaysLoading: boolean
}

export function ServiceRuleCard({
  serviceRuleEnabled,
  serviceRuleType,
  metadataOptions,
  holidays,
  isHolidaysLoading,
}: ServiceRuleCardProps) {
  const { token } = theme.useToken()

  function renderServiceThresholdInput() {
    if (!serviceRuleEnabled) return null
    if (serviceRuleType === 'Weekend')
      return (
        <Form.Item label='Time Range' style={{ flex: 1, marginBottom: 8 }}>
          <BBDTag theme2>{WEEKEND_TIME_RANGE}</BBDTag>
        </Form.Item>
      )
    if (serviceRuleType === 'Night')
      return (
        <Form.Item label='Time Range' style={{ flex: 1, marginBottom: 8 }}>
          <BBDTag theme2>{NIGHT_TIME_RANGE}</BBDTag>
        </Form.Item>
      )
    if (serviceRuleType === 'Holiday') {
      const holidayCollection = holidays?.[0]
      const holidayDates = holidayCollection?.dates || []
      const holidayCount = holidayDates.length || 0
      const holidayNames = holidayDates.map((h) => h.name).join(', ')
      return (
        <Form.Item label='Holidays' style={{ flex: 1, marginBottom: 8 }}>
          <Tooltip title={isHolidaysLoading ? 'Loading holidays...' : holidayNames || 'No holidays configured'}>
            <BBDTag theme2>
              Holidays <span style={{ marginLeft: 4 }}>{isHolidaysLoading ? '(loading...)' : `(${holidayCount})`}</span>
            </BBDTag>
          </Tooltip>
        </Form.Item>
      )
    }
    return null
  }

  return (
    <Card
      size='small'
      title={
        <Horizontal verticalCenter gap={8} flex='0' style={{ overflow: 'visible' }}>
          {getRuleIcon('Service Time')}
          <Texto weight='bold'>Service Time Rule</Texto>
        </Horizontal>
      }
      extra={
        <Form.Item name={['service_rule_form', 'enabled']} valuePropName='checked' noStyle>
          <Switch />
        </Form.Item>
      }
      style={{
        borderColor: serviceRuleEnabled ? token.colorWarning : token.colorBorderSecondary,
        boxShadow: serviceRuleEnabled ? `0 0 0 2px ${token.colorWarningBg}` : 'none',
        marginBottom: 0,
      }}
      bodyStyle={{ padding: serviceRuleEnabled ? '12px' : '8px' }}
    >
      {serviceRuleEnabled ? (
        <Vertical gap={8} flex='0' style={{ overflow: 'visible' }}>
          <Horizontal gap={8} flex='0' style={{ overflow: 'visible' }}>
            <Form.Item
              name={['service_rule_form', 'type']}
              label='Service Type'
              rules={[
                {
                  required: serviceRuleEnabled,
                  message: 'Please select a service type',
                },
              ]}
              style={{ flex: 1, marginBottom: 8 }}
            >
              <Select
                placeholder='Select service type'
                options={metadataOptions?.ratebook_options?.accessorials?.service_triggers?.map((event: string) => ({
                  label: event,
                  value: event,
                }))}
              />
            </Form.Item>
            {renderServiceThresholdInput()}
          </Horizontal>
          <Form.Item name={['service_rule_form', 'threshold_type']} hidden initialValue='time'>
            <Input />
          </Form.Item>
        </Vertical>
      ) : (
        <Texto appearance='hint'>Enable to configure service time rules</Texto>
      )}
    </Card>
  )
}
